<template>
	<div class="employee-container">
		<div class="main-header">
			<div class="header-title">
				<i class="el-icon-dangan"></i>
				<span>员工档案人员信息</span>
				<el-button v-if="$store.getters.hasPermission('system:staff:add')" type="primary" size="mini"
					icon="el-icon-plus" class="add-btn" @click="handleAddEmployee">
					添加员工
				</el-button>
			</div>
		</div>

		<div class="content-wrapper">
			<div class="left-aside">
				<div class="left-header">
					<div class="filter-container">
						<el-dropdown trigger="click" @command="handleStatusChange">
							<span class="el-dropdown-link">
								{{ currentStatus.label }}<i class="el-icon-arrow-down el-icon--right"></i>
							</span>
							<el-dropdown-menu slot="dropdown">
								<el-dropdown-item v-for="status in statusOptions" :key="status.id" :command="status">
									{{ status.label }}
								</el-dropdown-item>
							</el-dropdown-menu>
						</el-dropdown>

						<el-input v-model="searchName" class="search-input" placeholder="请输入姓名" clearable size="mini"
							@input="handleSearch">
						</el-input>
					</div>
				</div>

				<div class="employee-list" @click="handleListClick">
					<div v-for="(item, index) in sortedEmployeeList" 
						:key="item.id" 
						class="employee-item"
						:class="{ active: currentEmployee && currentEmployee.id === item.id }"
						@click="handleEmployeeClick(item)"
						@contextmenu.prevent="handleRightClick($event, item)">
						<div class="avatar-container">
							<el-avatar v-if="item.workIdPhoto" 
								:size="36" 
								:src="item.workIdPhoto" 
								:alt="item.fullName">
							</el-avatar>
							<el-avatar v-else 
								:size="36" 
								:style="{ backgroundColor: '#409eff' }">
								{{ item && item.fullName ? item.fullName.charAt(0) : '' }}
							</el-avatar>
						</div>
						<div class="info-container">
							<div class="name">
								{{ item && item.fullName ? item.fullName : '' }}
								<el-badge v-if="isCurrentUser(item)" value="我" class="badge-me" />
							</div>
							<div class="role">{{ getDeptDisplay(item) }}</div>
						</div>
					</div>
				</div>
				<div class="pagination-container">
					<el-pagination small layout="prev, pager, next" :page-size="pagination.pageSize"
						:current-page="pagination.pageNum" :total="pagination.total" @current-change="handlePageChange">
					</el-pagination>
				</div>
			</div>

			<div class="right-main">
				<div class="content-left">
					<template v-if="loading">
						<div class="skeleton-container">
							<el-skeleton :rows="8" animated :loading="loading" />
						</div>
					</template>
					<template v-else>
						<div v-if="Object.keys(currentEmployee).length || isAddMode" class="employee-detail">
							<div class="detail-header">
								<el-avatar v-if="!isAddMode && currentEmployee.workIdPhoto" :size="60"
									:src="currentEmployee.workIdPhoto" :alt="currentEmployee.fullName"></el-avatar>
								<el-avatar v-else-if="!isAddMode" :size="60"
									:style="{ backgroundColor: '#409eff', fontSize: '20px' }">
									{{ currentEmployee.fullName ? currentEmployee.fullName.charAt(0) : '' }}
								</el-avatar>
								<el-upload v-else class="avatar-uploader" action="#" :show-file-list="false"
									:before-upload="handleWorkIdPhotoUpload">
									<el-avatar v-if="form.workIdPhoto" :size="60" :src="form.workIdPhoto"
										class="avatar" />
									<i v-else class="el-icon-plus avatar-uploader-icon"></i>
								</el-upload>
								<div class="detail-basic">
									<h2 class="detail-name">{{ isAddMode ? '新增员工' : currentEmployee.fullName }}</h2>
									<div class="detail-role">
										{{ isAddMode ? '请填写员工信息' : getDeptDisplay(currentEmployee) }}</div>
								</div>
								<el-button v-if="!isAddMode && !isEditMode" type="primary" size="mini"
									icon="el-icon-edit" class="edit-btn" @click="handleEdit">
									修改
								</el-button>
							</div>

							<el-divider></el-divider>

							<div class="detail-main-container">
								<!-- 左侧可滚动内容区域 -->
								<div class="scrollable-content-area">
									<!-- 基本信息模块 -->
									<div class="detail-module">
										<h3 class="module-title">基本信息</h3>
										<div class="module-content">
											<div class="detail-info">
												<div class="info-row">
													<div class="info-item">
														<span class="label"><span class="required">*</span>姓名：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.fullName || '' }}</span>
														<el-input v-else v-model="form.fullName" size="mini"
															placeholder="请输入姓名"></el-input>
													</div>
													<div class="info-item">
														<span class="label">性别：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getSexText(currentEmployee.sex) }}</span>
														<el-select v-else v-model="form.sex" size="mini" placeholder="请选择性别">
															<el-option v-for="item in sexOptions" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">证件类型：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('certypeEntities', currentEmployee.idType) || '' }}</span>
														<el-select v-else v-model="form.idType" size="mini"
															placeholder="请选择证件类型">
															<el-option v-for="item in dictData.certypeEntities" :key="item.id"
																:label="item.name" :value="item.id"
																:disabled="item.id !== 1"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label"><span class="required">*</span>身份证号：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.idNumber || '' }}</span>
														<el-input v-else v-model="form.idNumber" size="mini"
															placeholder="请输入身份证号" :disabled="isEditMode"
															@blur="handleIdNumberBlur"></el-input>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">出生日期：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.birthDate || '' }}</span>
														<el-input v-else v-model="form.birthDate" size="mini"
															placeholder="系统自动计算" disabled></el-input>
													</div>
													<div class="info-item">
														<span class="label">农历生日：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.lunarBirthDate || '' }}</span>
														<el-input v-else v-model="form.lunarBirthDate" size="mini"
															placeholder="如：正月初一"></el-input>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">国籍：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('country', currentEmployee.nationality) || '' }}</span>
														<el-select v-else v-model="form.nationality" size="mini"
															placeholder="请选择国籍">
															<el-option v-for="item in dictData.country" :key="item.id"
																:label="item.name" :value="item.id"
																:disabled="item.id !== 1"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label">民族：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('natyEntities', currentEmployee.minZu) || '' }}</span>
														<el-select v-else v-model="form.minZu" size="mini" placeholder="请选择民族">
															<el-option v-for="item in dictData.natyEntities" :key="item.id"
																:label="item.name" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">籍贯：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.nativePlace || '' }}</span>
														<el-cascader v-else v-model="nativePlaceCascader" :options="areaOptions"
															:props="cascaderProps" size="mini" placeholder="请选择籍贯"
															style="width: 100%;"
															@change="handleNativePlaceChange"></el-cascader>
													</div>
													<div class="info-item">
														<span class="label">政治面貌：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('jobEntities', currentEmployee.politicalStatus) || '' }}</span>
														<el-select v-else v-model="form.politicalStatus" size="mini"
															placeholder="请选择政治面貌">
															<el-option v-for="item in dictData.jobEntities" :key="item.id"
																:label="item.name" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">血型：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('aboTypeEntities', currentEmployee.bloodType) || '' }}</span>
														<el-select v-else v-model="form.bloodType" size="mini"
															placeholder="请选择血型">
															<el-option v-for="item in dictData.aboTypeEntities" :key="item.id"
																:label="item.name" :value="item.id"
																:disabled="['RH阴性', 'RH阳性'].includes(item.name)"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label">婚姻状况：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('mrgStateEntities', currentEmployee.maritalStatus) || '' }}</span>
														<el-select v-else v-model="form.maritalStatus" size="mini"
															placeholder="请选择婚姻状况">
															<el-option v-for="item in dictData.mrgStateEntities" :key="item.id"
																:label="item.name" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">学历：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('edulBacdEntities', currentEmployee.educationalBackground) || '' }}</span>
														<el-select v-else v-model="form.educationalBackground" size="mini"
															placeholder="请选择学历">
															<el-option v-for="item in dictData.edulBacdEntities" :key="item.id"
																:label="item.name" :value="item.id"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label"><span class="required">*</span>专业职称：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getDictText('profttlEntities', currentEmployee.professionalTitle) || '' }}</span>
														<el-select v-else v-model="form.professionalTitle" size="mini"
															placeholder="请选择专业职称">
															<el-option v-for="item in dictData.profttlEntities" :key="item.id"
																:label="item.name" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label"><span class="required">*</span>联系电话：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.contactNumber || '' }}</span>
														<el-input v-else v-model="form.contactNumber" size="mini"
															placeholder="请输入联系电话" :disabled="isEditMode"></el-input>
													</div>
													<div class="info-item">
														<span class="label">状态：</span>
														<span v-if="!isEditMode && !isAddMode" class="value">
															<el-tag :type="currentEmployee.status === 1 ? 'success' : 'danger'"
																size="small">
																{{ getStatusText(currentEmployee.status) }}
															</el-tag>
														</span>
														<el-switch v-else v-model="form.status" :active-value="1"
															:inactive-value="0" active-color="#13ce66" inactive-color="#ff4949">
														</el-switch>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">联系地址：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.contactAddress || '' }}</span>
														<el-input v-else v-model="form.contactAddress" size="mini"
															placeholder="请输入联系地址"></el-input>
													</div>
												</div>
											</div>
										</div>
									</div>

									<!-- 权限信息模块 -->
									<div class="detail-module">
										<h3 class="module-title">权限信息</h3>
										<div class="module-content">
											<div class="detail-info">
												<div class="info-row">
													<div class="info-item">
														<span class="label">精神权限：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getPermissionText(currentEmployee.spiritPermission) || '' }}</span>
														<el-select v-else v-model="form.spiritPermission" size="mini"
															placeholder="请选择精神权限">
															<el-option v-for="item in YNOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label">抗生素等级：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getAntibioticLevelText(currentEmployee.antibioticAuthorization) || '' }}</span>
														<el-select v-else v-model="form.antibioticAuthorization" size="mini"
															placeholder="请选择抗生素等级">
															<el-option v-for="item in KssOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">处方权：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getPermissionText(currentEmployee.prescriptionRights) || '' }}</span>
														<el-select v-else v-model="form.prescriptionRights" size="mini"
															placeholder="请选择处方权">
															<el-option v-for="item in YNOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label">中药处方权：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getPermissionText(currentEmployee.chPrescriptionRights) || '' }}</span>
														<el-select v-else v-model="form.chPrescriptionRights" size="mini"
															placeholder="请选择中药处方权">
															<el-option v-for="item in YNOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">麻药权限：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getPermissionText(currentEmployee.anesthesiaAuthorization) || '' }}</span>
														<el-select v-else v-model="form.anesthesiaAuthorization" size="mini"
															placeholder="请选择麻药权限">
															<el-option v-for="item in YNOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label">手术权限：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getPermissionText(currentEmployee.surgicalPermission) || '' }}</span>
														<el-select v-else v-model="form.surgicalPermission" size="mini"
															placeholder="请选择手术权限">
															<el-option v-for="item in YNOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">毒药权限：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getPermissionText(currentEmployee.poisonPermission) || '' }}</span>
														<el-select v-else v-model="form.poisonPermission" size="mini"
															placeholder="请选择毒药权限">
															<el-option v-for="item in YNOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
													<div class="info-item">
														<span class="label">抗肿瘤权限：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ getPermissionText(currentEmployee.antiTumorPermission) || '' }}</span>
														<el-select v-else v-model="form.antiTumorPermission" size="mini"
															placeholder="请选择抗肿瘤权限">
															<el-option v-for="item in YNOption" :key="item.id"
																:label="item.label" :value="item.id"></el-option>
														</el-select>
													</div>
												</div>
											</div>
										</div>
									</div>

									<!-- 证件信息模块 -->
									<div class="detail-module">
										<h3 class="module-title">证件信息</h3>
										<div class="module-content">
											<div class="certificate-info">
												<div class="info-row">
													<div class="info-item">
														<span class="label">职业证号：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.practicingNumber || '' }}</span>
														<el-input v-else v-model="form.practicingNumber" size="mini"
															placeholder="请输入职业证号"></el-input>
													</div>
													<div class="info-item">
														<span class="label">职业日期：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.practiceDate || '' }}</span>
														<el-date-picker v-else v-model="form.practiceDate" type="date"
															size="mini" placeholder="选择职业日期"
															style="width: 100%;"></el-date-picker>
													</div>
												</div>

												<div class="info-row">
													<div class="info-item">
														<span class="label">资格证号：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.qualificationNumber || '' }}</span>
														<el-input v-else v-model="form.qualificationNumber" size="mini"
															placeholder="请输入资格证号"></el-input>
													</div>
												</div>

												<div class="certificate-images">
													<div class="image-item">
														<div class="image-title">工作证件照</div>
														<div class="image-container">
															<img v-if="!isEditMode && !isAddMode && currentEmployee.workIdPhoto"
																:src="currentEmployee.workIdPhoto" alt="工作证件照"
																class="certificate-image" />
															<img v-else-if="(isEditMode || isAddMode) && form.workIdPhoto"
																:src="form.workIdPhoto" alt="工作证件照" class="certificate-image" />
															<el-upload v-else class="image-uploader" action="#"
																:show-file-list="false"
																:before-upload="(file) => handleImageUpload(file, 'workIdPhoto')">
																<div class="image-empty">
																	<i class="el-icon-plus"></i>
																	<span>点击上传</span>
																</div>
															</el-upload>
														</div>
													</div>
													<div class="image-item">
														<div class="image-title">手工签名图片</div>
														<div class="image-container">
															<img v-if="!isEditMode && !isAddMode && currentEmployee.signatureImage"
																:src="currentEmployee.signatureImage" alt="手工签名"
																class="certificate-image" />
															<img v-else-if="(isEditMode || isAddMode) && form.signatureImage"
																:src="form.signatureImage" alt="手工签名"
																class="certificate-image" />
															<el-upload v-else class="image-uploader" action="#"
																:show-file-list="false"
																:before-upload="(file) => handleImageUpload(file, 'signatureImage')">
																<div class="image-empty">
																	<i class="el-icon-plus"></i>
																	<span>点击上传</span>
																</div>
															</el-upload>
														</div>
													</div>
													<div class="image-item">
														<div class="image-title">资格证电子图片</div>
														<div class="image-container">
															<img v-if="!isEditMode && !isAddMode && currentEmployee.qcElectronicImage"
																:src="currentEmployee.qcElectronicImage" alt="资格证"
																class="certificate-image" />
															<img v-else-if="(isEditMode || isAddMode) && form.qcElectronicImage"
																:src="form.qcElectronicImage" alt="资格证"
																class="certificate-image" />
															<el-upload v-else class="image-uploader" action="#"
																:show-file-list="false"
																:before-upload="(file) => handleImageUpload(file, 'qcElectronicImage')">
																<div class="image-empty">
																	<i class="el-icon-plus"></i>
																	<span>点击上传</span>
																</div>
															</el-upload>
														</div>
													</div>
													<div class="image-item">
														<div class="image-title">执业证电子图片</div>
														<div class="image-container">
															<img v-if="!isEditMode && !isAddMode && currentEmployee.pcElectronicImage"
																:src="currentEmployee.pcElectronicImage" alt="执业证"
																class="certificate-image" />
															<img v-else-if="(isEditMode || isAddMode) && form.pcElectronicImage"
																:src="form.pcElectronicImage" alt="执业证"
																class="certificate-image" />
															<el-upload v-else class="image-uploader" action="#"
																:show-file-list="false"
																:before-upload="(file) => handleImageUpload(file, 'pcElectronicImage')">
																<div class="image-empty">
																	<i class="el-icon-plus"></i>
																	<span>点击上传</span>
																</div>
															</el-upload>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>

									<!-- 医保信息模块 -->
									<div class="detail-module">
										<h3 class="module-title">医保信息</h3>
										<div class="module-content">
											<div class="detail-info">
												<div class="info-row">
													<div class="info-item">
														<span class="label">医保编码：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.standardCode || '' }}</span>
														<el-input v-else v-model="form.standardCode" size="mini"
															placeholder="请输入医保编码"></el-input>
													</div>
													<div class="info-item">
														<span class="label">医保名称：</span>
														<span v-if="!isEditMode && !isAddMode"
															class="value">{{ currentEmployee.standardName || '' }}</span>
														<el-input v-else v-model="form.standardName" size="mini"
															placeholder="请输入医保名称"></el-input>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<!-- 固定在底部的科室模块 -->
								<div class="fixed-bottom-area">
									<div class="detail-module dept-module">
										<div class="dept-module-header">
											<h3 class="module-title">当前科室 <span v-if="isEditMode || isAddMode" class="required">*</span></h3>
											<el-button v-if="isEditMode || isAddMode" type="text" size="mini" @click="showAddDeptDialog = true">
												添加科室
											</el-button>
										</div>
										<div class="module-content">
											<el-table :data="deptTableData" size="mini" style="width: 100%"
												:header-cell-style="{ background: '#f5f7fa', color: '#606266' }">
												<el-table-column type="index" label="行号" width="60">
												</el-table-column>
												<el-table-column prop="deptName" label="科室名称" min-width="120">
													<template slot-scope="scope">
														<span v-if="!scope.row.editing">{{ scope.row.deptName }}</span>
														<el-select v-else v-model="scope.row.deptName" size="mini" placeholder="请选择科室"
															style="width: 100%;">
															<el-option v-for="dept in deptOptions" :key="dept.id" :label="dept.deptName"
																:value="dept.deptName"></el-option>
														</el-select>
													</template>
												</el-table-column>
												<el-table-column prop="mainDep" label="主要科室" width="100">
													<template slot-scope="scope">
														<span v-if="!scope.row.editing">{{ scope.row.mainDep === 1 ? '是' : '否' }}</span>
														<el-select v-else v-model="scope.row.mainDep" size="mini" style="width: 100%;">
															<el-option v-for="item in YNOption" :key="item.id" :label="item.label" :value="item.id"></el-option>
														</el-select>
													</template>
												</el-table-column>
												<el-table-column v-if="isEditMode || isAddMode" label="操作" width="120">
													<template slot-scope="scope">
														<template v-if="!scope.row.editing">
															<el-button type="text" size="mini" @click="handleDeptEdit(scope.row)">
																修改
															</el-button>
															<el-button type="text" size="mini" @click="handleDeptRemove(scope.row)">
																移除
															</el-button>
														</template>
														<template v-else>
															<el-button type="text" size="mini" @click="saveDeptEdit(scope.row)">
																保存
															</el-button>
															<el-button type="text" size="mini" @click="cancelDeptEdit(scope.row)">
																取消
															</el-button>
														</template>
													</template>
												</el-table-column>
											</el-table>

											<!-- 在add-dept-dialog中添加主要科室提示 -->
											<div v-if="showAddDeptDialog && (isEditMode || isAddMode)" class="add-dept-dialog">
											  <div class="dept-form">
											    <el-select v-model="newDept.deptName" size="mini" placeholder="请选择科室"
											      style="margin-right: 10px; width: 150px;">
											      <el-option v-for="dept in deptOptions" :key="dept.id" :label="dept.deptName"
											        :value="dept.deptName"></el-option>
											    </el-select>
											    <el-select v-model="newDept.mainDep" size="mini" placeholder="主要科室"
											      style="width: 100px;">
											      <el-option v-for="item in YNOption" :key="item.id"
											        :label="item.label" :value="item.id"
											        :disabled="item.id === 1 && deptTableData.some(dept => dept.mainDep === 1)">
											      </el-option>
											    </el-select>
											  </div>
											  <div v-if="deptTableData.some(dept => dept.mainDep === 1)" class="main-dept-tip">
											    <i class="el-icon-info" style="color: #e6a23c;"></i>
											    <span style="color: #e6a23c; font-size: 12px; margin-left: 4px;">
											      当前已设置主要科室，新增科室将设为非主要科室
											    </span>
											  </div>
											  <div v-else class="main-dept-tip">
											    <i class="el-icon-info" style="color: #409eff;"></i>
											    <span style="color: #409eff; font-size: 12px; margin-left: 4px;">
											      当前无主要科室，第一个科室将自动设为主要科室
											    </span>
											  </div>
											  <div class="dept-actions">
											    <el-button size="mini" @click="cancelAddDept">
											      取消
											    </el-button>
											    <el-button type="primary" size="mini" @click="saveNewDept">
											      保存
											    </el-button>
											  </div>
											</div>
										</div>
									</div>

									<!-- 操作按钮 -->
									<div v-if="isEditMode || isAddMode" class="action-buttons">
										<el-button size="mini" @click="handleCancel">取消</el-button>
										<el-button type="primary" size="mini" @click="handleSave">
											{{ isAddMode ? '完成建档' : '保存' }}
										</el-button>
									</div>
								</div>
							</div>
						</div>

						<div v-else class="empty-state">
							<el-empty description="请选择员工查看详细信息"></el-empty>
						</div>
					</template>
				</div>
			</div>
		</div>

		<!-- 右键菜单 -->
		<div v-if="rightMenuVisible" 
			:style="{
				position: 'fixed',
				left: rightMenuStyle.left + 'px',
				top: rightMenuStyle.top + 'px',
				zIndex: 9999
			}" 
			class="right-context-menu"
			@click.stop>
			<el-card shadow="always" style="width: 120px; padding: 0;">
				<div class="right-menu-content">
					<el-button type="text" size="mini" @click="handleRightMenuItemClick('detail')"
						style="width: 100%; text-align: left; padding: 8px 12px; font-size: 12px;">
						<i class="el-icon-view"></i>详情
					</el-button>
					<el-button v-if="isCurrentUser(rightClickEmployee)" 
						type="text" size="mini" @click="handleRightMenuItemClick('bindRole')"
						style="width: 100%; text-align: left; padding: 8px 12px; font-size: 12px;">
						<i class="el-icon-s-tools"></i>绑定角色
					</el-button>
					<el-button v-if="isCurrentUser(rightClickEmployee)" 
						type="text" size="mini" @click="handleRightMenuItemClick('resetPassword')"
						style="width: 100%; text-align: left; padding: 8px 12px; font-size: 12px;">
						<i class="el-icon-lock"></i>修改密码
					</el-button>
					<el-button type="text" size="mini" @click="handleRightMenuItemClick('edit')"
						style="width: 100%; text-align: left; padding: 8px 12px; font-size: 12px;">
						<i class="el-icon-edit"></i>修改档案
					</el-button>
				</div>
			</el-card>
		</div>

		<!-- 绑定角色对话框 -->
		<el-dialog title="绑定角色" :visible.sync="bindRoleDialogVisible" width="500px" @opened="handleBindRoleDialogOpen">
			<div style="margin-bottom: 15px;">为 {{ rightClickEmployee ? rightClickEmployee.fullName : '' }} 绑定角色</div>
			<el-form :model="roleForm" :rules="roleRules" ref="roleFormRef">
				<el-form-item label="角色选择" prop="selectedRoleIds">
					<el-select
						v-model="roleForm.selectedRoleIds"
						multiple
						filterable
						placeholder="请选择角色"
						style="width: 100%;"
						:loading="loadingRoles"
					>
						<el-option
							v-for="role in roleOptions"
							:key="role.id"
							:label="role.roleName"
							:value="role.id"
						></el-option>
					</el-select>
				</el-form-item>
			</el-form>
			<div slot="footer" class="dialog-footer">
				<el-button @click="bindRoleDialogVisible = false; resetRoleForm()">取消</el-button>
				<el-button type="primary" @click="handleBindRoleSubmit" :loading="bindingRole">确认绑定</el-button>
			</div>
		</el-dialog>

		<!-- 修改密码对话框 -->
		<el-dialog title="重置我的本系统登录密码" :visible.sync="resetPasswordDialogVisible" width="400px">
			<div style="margin-bottom: 15px;">为 {{ rightClickEmployee ? rightClickEmployee.fullName : '' }} 重置密码</div>
			<el-form :model="passwordForm" :rules="passwordRules" ref="passwordFormRef">
				<el-form-item label="新密码" prop="newPassword">
					<el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码" show-password></el-input>
				</el-form-item>
				<el-form-item label="确认密码" prop="confirmPassword">
					<el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请确认新密码" show-password></el-input>
				</el-form-item>
			</el-form>
			<div slot="footer" class="dialog-footer">
				<el-button @click="resetPasswordDialogVisible = false; resetPasswordForm()">取消</el-button>
				<el-button type="primary" @click="handleResetPassword" :loading="resettingPassword">确定重置</el-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>
	import {
		selectList,
		dict,
		updateStatus
	} from "@/api/staff/staff.js"
	import { GetUserInfo, roleList, updateUser, editPwd, findAttribute, getArea, addStaff, updateStaff, getStaff, builderImage } from "@/api/user.js"

	export default {
		name: 'Employee',
		data() {
			return {
				loading: false,
				searchName: '',
				currentEmployee: {},
				currentStatus: {
					label: '全部',
					value: ''
				},
				statusOptions: [{
						id: '',
						label: '全部',
						value: ''
					},
					{
						id: 1,
						label: '正常',
						value: 1
					},
					{
						id: 0,
						label: '异常',
						value: 0
					}
				],
				employeeList: [],
				resettingPassword: false,
				passwordForm: {
					newPassword: '',
					confirmPassword: ''
				},
				passwordRules: {
					newPassword: [
						{ required: true, message: '请输入新密码', trigger: 'blur' },
						{ min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
					],
					confirmPassword: [
						{ required: true, message: '请确认新密码', trigger: 'blur' },
						{ validator: this.validateConfirmPassword, trigger: 'blur' }
					]
				},
				YNOption: [{ id: 0, label: '否' }, { id: 1, label: '是' }],
				KssOption: [{ id: 1, label: '一级' }, { id: 2, label: '二级' }, { id: 3, label: '三级' }],
				pagination: {
					pageNum: 1,
					pageSize: 15,
					total: 0
				},
				rightMenuVisible: false,
				rightMenuStyle: {
					left: 0,
					top: 0
				},
				isEditMode: false,
				isAddMode: false,
				form: {
					anesthesiaAuthorization: 0,
					antiTumorPermission: 0,
					antibioticAuthorization: 0,
					birthDate: '',
					bloodType: '',
					chPrescriptionRights: 0,
					clinicId: null,
					contactAddress: '',
					contactNumber: '',
					dateOfEmp: '',
					deptList: [],
					educationalBackground: '',
					entryTime: '',
					fullName: '',
					idNumber: '',
					idType: '',
					lunarBirthDate: '',
					major: '',
					maritalStatus: '',
					minZu: '',
					nationality: '',
					nativePlace: '',
					pcElectronicImage: '',
					poisonPermission: 0,
					politicalStatus: '',
					practiceDate: '',
					practicingNumber: '',
					prescriptionRights: 0,
					professionalTitle: '',
					qcElectronicImage: '',
					qualificationNumber: '',
					resignationTime: '',
					roleId: [],
					sex: '',
					signatureImage: '',
					spiritPermission: 0,
					staffAtte: '',
					standardCode: '',
					standardName: '',
					status: 1,
					surgicalPermission: 0,
					workIdPhoto: ''
				},
				deptTableData: [],
				dictData: {
					certypeEntities: [],
					natyEntities: [],
					country: [],
					jobEntities: [],
					aboTypeEntities: [],
					mrgStateEntities: [],
					edulBacdEntities: [],
					profttlEntities: []
				},
				areaOptions: [],
				sexOptions: [
					{ id: 0, label: '未知' },
					{ id: 1, label: '男' },
					{ id: 2, label: '女' },
					{ id: 9, label: '未说明' }
				],
				cascaderProps: {
					value: 'areaName',
					label: 'areaName',
					children: 'children',
					lazy: true,
					lazyLoad: this.loadAreaData
				},
				nativePlaceCascader: [],
				rightClickEmployee: null,
				bindRoleDialogVisible: false,
				resetPasswordDialogVisible: false,
				showAddDeptDialog: false,
				deptOptions: [],
				newDept: {
					deptName: '',
					mainDep: 0
				},
				userInfo: JSON.parse(localStorage.getItem('userInfo') || '{}'),
				searchTimer: null,
				// 角色绑定相关数据
				roleOptions: [],
				loadingRoles: false,
				bindingRole: false,
				roleForm: {
					selectedRoleIds: []
				},
				roleRules: {
					selectedRoleIds: [
						{ required: true, message: '请至少选择一个角色', trigger: 'change' }
					]
				}
			}
		},
		mounted() {
			// 确保数据有初始值
			this.employeeList = []
			this.dictData = {
				certypeEntities: [],
				natyEntities: [],
				country: [],
				jobEntities: [],
				aboTypeEntities: [],
				mrgStateEntities: [],
				edulBacdEntities: [],
				profttlEntities: []
			}

			// 初始化完成后加载数据
			this.$nextTick(() => {
				this.fetchEmployeeList()
				this.fetchDictData()
			})
		},
		beforeDestroy() {
			document.removeEventListener('click', this.handleGlobalClick);
			window.removeEventListener('resize', this.handleWindowResize);
		},
		computed: {
			sortedEmployeeList() {
				const list = [...this.employeeList]
				const currentUserId = this.userInfo.userId

				// 找到当前用户的索引
				const currentUserIndex = list.findIndex(item =>
					item.id === currentUserId && item.fullName === this.userInfo.staffName
				)

				if (currentUserIndex > -1) {
					const [currentUser] = list.splice(currentUserIndex, 1)
					return [currentUser, ...list]
				}

				return list
			}
		},
		methods: {
			// 获取员工列表
			async fetchEmployeeList() {
				this.loading = true
				try {
					const params = {
						fullName: this.searchName || '',
						pageNum: this.pagination.pageNum,
						pageSize: this.pagination.pageSize,
						status: this.currentStatus.value
					}

					const res = await selectList(params)
					if (res && res.data && res.data.content) {
						this.employeeList = res.data.content
						this.pagination.total = res.data.totalElements || 0

						// 找到当前用户
						const currentUserIndex = this.employeeList.findIndex(item =>
							this.isCurrentUser(item)
						)

						// 如果当前用户在列表中，则默认选中当前用户
						if (currentUserIndex > -1) {
							this.currentEmployee = this.employeeList[currentUserIndex]
							await this.fetchEmployeeDetail(this.currentEmployee.id)
						}
						// 否则默认选中第一个员工
						else if (this.employeeList.length > 0 && !this.isAddMode) {
							this.currentEmployee = this.employeeList[0]
							await this.fetchEmployeeDetail(this.currentEmployee.id)
						}
					}
				} catch (error) {
					console.error('获取员工列表失败:', error)
					this.$message.error('获取员工列表失败')
				} finally {
					this.loading = false
				}
			},

			// 获取员工详情
			async fetchEmployeeDetail(id) {
				try {
					const res = await getStaff({
						id
					})
					if (res && res.data) {
						this.currentEmployee = res.data
						this.deptTableData = (res.data.deptList || []).map(dept => ({
							...dept,
							editing: false
						}))
					}
				} catch (error) {
					console.error('获取员工详情失败:', error)
				}
			},

			// 获取字典数据
			async fetchDictData() {
				try {
					const res = await dict()
					if (res && res.data) {
						this.dictData = {
							certypeEntities: res.data.certypeEntities || [],
							natyEntities: res.data.natyEntities || [],
							country: res.data.country || [],
							jobEntities: res.data.jobEntities || [],
							aboTypeEntities: res.data.aboTypeEntities || [],
							mrgStateEntities: res.data.mrgStateEntities || [],
							edulBacdEntities: res.data.edulBacdEntities || [],
							profttlEntities: res.data.profttlEntities || []
						}

						// 设置默认证件类型为居民身份证
						const residentIdCard = this.dictData.certypeEntities.find(item => item.id === 1 && item
							.name === '居民身份证')
						if (residentIdCard) {
							this.form.idType = residentIdCard.id
						}

						// 设置默认国籍为中国
						const china = this.dictData.country.find(item => item.id === 1 && item.name === '中国')
						if (china) {
							this.form.nationality = china.id
						}
					}
				} catch (error) {
					console.error('获取字典数据失败:', error)
				}
			},

			// 懒加载地区数据
			async loadAreaData(node, resolve) {
				const {
					level,
					data
				} = node

				if (level === 0) {
					// 加载第一级数据
					try {
						const res = await getArea({
							areaCode: '',
							areaLevel: 1,
							condtion: ''
						})
						if (res && res.data) {
							const nodes = res.data.map(area => ({
								...area,
								leaf: false
							}))
							resolve(nodes)
						} else {
							resolve([])
						}
					} catch (error) {
						console.error('获取地区数据失败:', error)
						resolve([])
					}
				} else if (level === 1) {
					// 加载第二级数据
					try {
						const res = await getArea({
							areaCode: data.areaCode,
							areaLevel: 2,
							condtion: 'area_p_code'
						})
						if (res && res.data) {
							const nodes = res.data.map(area => ({
								...area,
								leaf: false
							}))
							resolve(nodes)
						} else {
							resolve([])
						}
					} catch (error) {
						console.error('获取二级地区数据失败:', error)
						resolve([])
					}
				} else if (level === 2) {
					// 加载第三级数据
					try {
						const res = await getArea({
							areaCode: data.areaCode,
							areaLevel: 3,
							condtion: 'area_c_code'
						})
						if (res && res.data) {
							const nodes = res.data.map(area => ({
								...area,
								leaf: true
							}))
							resolve(nodes)
						} else {
							resolve([])
						}
					} catch (error) {
						console.error('获取三级地区数据失败:', error)
						resolve([])
					}
				}
			},

			// 获取科室选项
			async fetchDeptOptions() {
				try {
					const clinicId = this.userInfo.clinicId
					if (clinicId) {
						const res = await findAttribute({
							clinicId
						})
						if (res && res.data) {
							this.deptOptions = res.data
						}
					}
				} catch (error) {
					console.error('获取科室选项失败:', error)
				}
			},

			// 获取字典文本
			getDictText(dictKey, value) {
				if (!dictKey || value === undefined || value === null) {
					return ''
				}

				if (!this.dictData || !this.dictData[dictKey] || !Array.isArray(this.dictData[dictKey])) {
					return ''
				}

				const dictArray = this.dictData[dictKey]
				const item = dictArray.find(item => item && item.id == value)
				return item ? item.name : ''
			},

			// 获取科室显示文本
			getDeptDisplay(employee) {
				if (!employee || !employee.deptList || !Array.isArray(employee.deptList) || employee.deptList.length === 0) {
					return '未分配科室'
				}

				const deptNames = employee.deptList
					.filter(dept => dept && dept.deptName)
					.map(dept => dept.deptName)
					.filter(name => name && name.trim()) // 过滤空值

				if (!deptNames || deptNames.length === 0) {
					return '未分配科室'
				}

				if (deptNames.length <= 3) {
					return deptNames.join('、')
				} else {
					return `${deptNames.slice(0, 3).join('、')} (+${deptNames.length - 3})`
				}
			},

			// 获取性别文本
			getSexText(sex) {
				const sexMap = {
					'1': '男',
					'2': '女',
					'9': '未说明的性别',
					'0': '未知的性别'
				}
				return sexMap[sex] || ''
			},

			// 获取权限文本
			getPermissionText(permission) {
				if (permission === 1) return '是'
				if (permission === 0) return '否'
				return ''
			},

			// 获取抗生素等级文本
			getAntibioticLevelText(level) {
				const levelMap = {
					'1': '一级',
					'2': '二级',
					'3': '三级'
				}
				return levelMap[level] || ''
			},

			// 获取状态文本
			getStatusText(status) {
				if (status === 1) return '正常'
				if (status === 0) return '异常'
				return ''
			},

			// 获取员工属性文本
			getStaffAtteText(staffAtte) {
				const map = {
					'1': '医生',
					'2': '护士',
					'3': '技师',
					'4': '药师',
					'5': '其他'
				}
				return map[staffAtte] || ''
			},

			// 判断是否为当前用户
			isCurrentUser(employee) {
				if (!employee || !this.userInfo) return false
				return employee.id === this.userInfo.userId && employee.fullName === this.userInfo.staffName
			},

			// 处理身份证号输入完成
			async handleIdNumberBlur() {
				if (this.form.idNumber && this.form.idNumber.length === 18) {
					// 计算出生日期
					const birthDate = this.form.idNumber.substr(6, 8)
					const year = birthDate.substr(0, 4)
					const month = birthDate.substr(4, 2)
					const day = birthDate.substr(6, 2)
					this.form.birthDate = `${year}-${month}-${day}`

					// 计算性别
					const sexCode = parseInt(this.form.idNumber.substr(16, 1))
					this.form.sex = sexCode % 2 === 1 ? 1 : 2

					// 农历生日默认为空
					this.form.lunarBirthDate = ''
				}
			},

			// 处理籍贯选择
			handleNativePlaceChange(value) {
				if (value && value.length > 0) {
					this.form.nativePlace = value.join('/')
				}
			},

			// 处理工作证件照上传
			async handleWorkIdPhotoUpload(file) {
				return await this.handleImageUpload(file, 'workIdPhoto')
			},

			// 处理图片上传
			async handleImageUpload(file, field) {
				const isImage = file.type.startsWith('image/')
				if (!isImage) {
					this.$message.error('只能上传图片文件!')
					return false
				}
				const isLt2M = file.size / 1024 / 1024 < 2
				if (!isLt2M) {
					this.$message.error('图片大小不能超过 2MB!')
					return false
				}

				try {
					const formData = new FormData()
					formData.append('file', file)

					const res = await builderImage(formData)
					if (res && res.code === 200 && res.data && res.data.url) {
						this.form[field] = res.data.url
						this.$message.success('上传成功')

						// 如果上传的是工作证件照，更新头像显示
						if (field === 'workIdPhoto' && this.isAddMode) {
							// 头像已经通过头像上传组件更新
						}
					} else {
						this.$message.error('上传失败')
					}
				} catch (error) {
					console.error('上传图片失败:', error)
					this.$message.error('上传失败')
				}

				return false
			},

			// 搜索处理
			handleSearch() {
				if (this.searchTimer) {
					clearTimeout(this.searchTimer)
				}
				this.searchTimer = setTimeout(() => {
					this.pagination.pageNum = 1
					this.fetchEmployeeList()
				}, 500)
			},

			handleEmployeeClick(employee) {
				if (this.isEditMode || this.isAddMode) {
					this.$confirm('当前有未保存的修改，是否继续查看？', '提示', {
						confirmButtonText: '确定',
						cancelButtonText: '取消',
						type: 'warning'
					}).then(() => {
						this.resetForm()
						this.loadEmployeeDetail(employee)
					})
				} else {
					this.loadEmployeeDetail(employee)
				}
			},

			// 添加列表点击处理
			handleListClick(event) {
				// 如果点击的是列表本身（不是员工项），则关闭右键菜单
				if (event.target.classList.contains('employee-list')) {
					this.closeRightMenu();
				}
			},

			// 右键点击
			handleRightClick(event, employee) {
				event.preventDefault();
				event.stopPropagation();

				this.rightClickEmployee = employee;
				this.rightMenuVisible = false; // 先关闭

				// 获取点击坐标
				const clickX = event.clientX;
				const clickY = event.clientY;

				// 计算视口尺寸
				const viewportWidth = window.innerWidth;
				const viewportHeight = window.innerHeight;

				// 菜单预设尺寸
				const menuWidth = 120;
				const menuHeight = 160; // 4个菜单项，每个40px

				// 计算最终位置，防止超出视口
				let finalLeft = clickX;
				let finalTop = clickY;

				// 如果右侧空间不足，向左偏移
				if (clickX + menuWidth > viewportWidth) {
					finalLeft = clickX - menuWidth;
				}

				// 如果底部空间不足，向上偏移
				if (clickY + menuHeight > viewportHeight) {
					finalTop = clickY - menuHeight;
				}

				// 确保不超出视口边界
				finalLeft = Math.max(10, Math.min(finalLeft, viewportWidth - menuWidth - 10));
				finalTop = Math.max(10, Math.min(finalTop, viewportHeight - menuHeight - 10));

				// 设置菜单位置
				this.rightMenuStyle = {
					left: finalLeft,
					top: finalTop
				};

				// 显示菜单
				this.rightMenuVisible = true;

				// 添加全局点击监听
				setTimeout(() => {
					document.addEventListener('click', this.handleGlobalClick);
				}, 0);
			},

			// 添加全局点击处理
			handleGlobalClick(event) {
				const menuElement = document.querySelector('.right-context-menu');
				if (menuElement && !menuElement.contains(event.target)) {
					this.closeRightMenu();
				}
			},

			// 关闭菜单
			closeRightMenu() {
				this.rightMenuVisible = false;
				document.removeEventListener('click', this.handleGlobalClick);
			},

			// 右键菜单命令处理
			handleRightMenuItemClick(command) {
				this.closeRightMenu();
				setTimeout(() => {
					switch (command) {
						case 'detail':
							this.loadEmployeeDetail(this.rightClickEmployee);
							break;
						case 'bindRole':
							this.handleBindRole();
							break;
						case 'resetPassword':
							this.resetPasswordDialogVisible = true;
							break;
						case 'edit':
							this.handleEdit();
							break;
					}
				}, 50);
			},

			// 绑定角色处理
			async handleBindRole() {
				if (!this.rightClickEmployee || !this.rightClickEmployee.id) {
					this.$message.warning('请先选择员工')
					return
				}

				// 重置角色表单
				this.resetRoleForm()
				
				// 打开对话框
				this.bindRoleDialogVisible = true
			},

			// 绑定角色对话框打开时的处理
			async handleBindRoleDialogOpen() {
				if (!this.rightClickEmployee || !this.rightClickEmployee.id) {
					this.$message.warning('请先选择员工')
					return
				}

				// 加载角色列表
				await this.fetchRoleList()
				
				// 加载用户当前角色
				await this.fetchUserRoles()
			},

			// 获取角色列表
			async fetchRoleList() {
				this.loadingRoles = true
				try {
					const params = {
						clinicId: this.userInfo.clinicId,
						pageNum: 1,
						pageSize: 99
					}
					
					const res = await roleList(params);
					
					if (res) {
						this.roleOptions = res
					} else {
						this.$message.error(res?.msg || '获取角色列表失败')
						this.roleOptions = []
					}
				} catch (error) {
					console.error('获取角色列表失败:', error)
					this.$message.error('获取角色列表失败')
					this.roleOptions = []
				} finally {
					this.loadingRoles = false
				}
			},

			// 获取用户当前角色
			async fetchUserRoles() {
				try {
					const params = {
						userid: this.rightClickEmployee.id
					}
					
					const res = await GetUserInfo(params)
					if (res && res.code === 200 && res.data) {
						// 设置已选择的角色ID
						this.roleForm.selectedRoleIds = res.data.roleId || []
					} else {
						this.$message.error(res?.msg || '获取用户角色失败')
						this.roleForm.selectedRoleIds = []
					}
				} catch (error) {
					console.error('获取用户角色失败:', error)
					this.$message.error('获取用户角色失败')
					this.roleForm.selectedRoleIds = []
				}
			},

			// 提交绑定角色
			async handleBindRoleSubmit() {
				this.$refs.roleFormRef.validate(async (valid) => {
					if (!valid) return
					
					this.bindingRole = true
					try {
						const params = {
							email: '',
							roleId: this.roleForm.selectedRoleIds,
							status: 1,
							userId: this.rightClickEmployee.id
						}
						
						const res = await updateUser(params)
						if (res && res.code === 200) {
							this.$message.success(`已为${this.rightClickEmployee.fullName}成功绑定新的角色`)
							this.bindRoleDialogVisible = false
						} else {
							this.$message.error(res?.msg || '绑定角色失败')
						}
					} catch (error) {
						console.error('绑定角色失败:', error)
						this.$message.error('绑定角色失败')
					} finally {
						this.bindingRole = false
					}
				})
			},

			// 重置角色表单
			resetRoleForm() {
				this.roleForm = {
					selectedRoleIds: []
				}
				if (this.$refs.roleFormRef) {
					this.$refs.roleFormRef.clearValidate()
				}
			},
			
			// 添加科室验证方法
			validateDeptTable() {
			  // 检查是否有科室数据
			  if (!this.deptTableData || this.deptTableData.length === 0) {
			    this.$message.warning('请至少添加一个科室')
			    return false
			  }
			  
			  // 检查主要科室数量
			  const mainDepts = this.deptTableData.filter(dept => dept.mainDep === 1)
			  
			  // 第一个条件：必须有一个主要科室
			  if (mainDepts.length === 0) {
			    this.$message.warning('必须设置一个主要科室')
			    return false
			  }
			  
			  // 第二个条件：只能有一个主要科室
			  if (mainDepts.length > 1) {
			    this.$message.warning('只能设置一个主要科室，当前设置了 ' + mainDepts.length + ' 个')
			    return false
			  }
			  
			  return true
			},
			
			// 检查科室名称是否重复
			checkDuplicateDeptName(excludeId = null) {
			  const deptNames = this.deptTableData
			    .filter(dept => !excludeId || dept.id !== excludeId)
			    .map(dept => dept.deptName)
			    .filter(name => name && name.trim())
			  
			  const hasDuplicate = deptNames.length !== new Set(deptNames).size
			  if (hasDuplicate) {
			    this.$message.warning('科室名称不能重复')
			  }
			  return !hasDuplicate
			},
			
			loadEmployeeDetail(employee) {
				this.loading = true
				this.currentEmployee = employee
				this.isEditMode = false
				this.isAddMode = false
				this.fetchEmployeeDetail(employee.id)

				// 模拟加载延迟
				setTimeout(() => {
					this.loading = false
				}, 300)
			},

			handlePageChange(page) {
				this.pagination.pageNum = page
				this.fetchEmployeeList()
			},

			handleStatusChange(command) {
				this.currentStatus = command
				this.pagination.pageNum = 1
				this.fetchEmployeeList()
			},

			async handleRefresh() {
				this.loading = true
				try {
					await this.fetchEmployeeList()
					this.$message.success('刷新成功')
				} catch (error) {
					console.error('刷新失败:', error)
				} finally {
					this.loading = false
				}
			},

			handleEdit() {
				this.isEditMode = true
				this.isAddMode = false
				this.form = {
					...this.currentEmployee
				}
				this.deptTableData = [...(this.currentEmployee.deptList || [])].map(dept => ({
					...dept,
					editing: false
				}))
				this.fetchDeptOptions()

				// 设置籍贯的级联选择器值
				if (this.form.nativePlace) {
					this.nativePlaceCascader = this.form.nativePlace.split('/')
				}
			},

			handleAddEmployee() {
				this.isAddMode = true
				this.isEditMode = false
				this.currentEmployee = {}
				this.form = {
					anesthesiaAuthorization: 0,
					antiTumorPermission: 0,
					antibioticAuthorization: 0,
					birthDate: '',
					bloodType: '',
					chPrescriptionRights: 0,
					clinicId: this.userInfo.clinicId,
					contactAddress: '',
					contactNumber: '',
					dateOfEmp: '',
					deptList: [],
					educationalBackground: '',
					entryTime: '',
					fullName: '',
					idNumber: '',
					idType: this.dictData.certypeEntities.find(item => item.id === 1 && item.name === '居民身份证')
						?.id || '',
					lunarBirthDate: '',
					major: '',
					maritalStatus: '',
					minZu: '',
					nationality: this.dictData.country.find(item => item.id === 1 && item.name === '中国')?.id || '',
					nativePlace: '',
					pcElectronicImage: '',
					poisonPermission: 0,
					politicalStatus: '',
					practiceDate: '',
					practicingNumber: '',
					prescriptionRights: 0,
					professionalTitle: '',
					qcElectronicImage: '',
					qualificationNumber: '',
					resignationTime: '',
					roleId: [],
					sex: '',
					signatureImage: '',
					spiritPermission: 0,
					staffAtte: '',
					standardCode: '',
					standardName: '',
					status: 1,
					surgicalPermission: 0,
					workIdPhoto: ''
				}
				this.deptTableData = []
				this.showAddDeptDialog = false
				this.nativePlaceCascader = []
				this.fetchDeptOptions()
			},

			handleCancel() {
				this.resetForm()
				if (this.currentEmployee.id) {
					this.loadEmployeeDetail(this.currentEmployee)
				} else {
					this.currentEmployee = {}
					this.isEditMode = false
					this.isAddMode = false
					this.showAddDeptDialog = false
				}
			},

			async handleSave() {
				if (!this.validateForm()) {
					return
				}
				// 验证科室数据
				if (!this.validateDeptTable()) {
				    return
				}
				this.loading = true
				try {
					const formData = {
						...this.form,
						deptList: this.deptTableData.map(dept => ({
							deptId: dept.deptId,
							deptName: dept.deptName,
							mainDep: dept.mainDep
						}))
					}

					if (this.isAddMode) {
						const res = await addStaff(formData)
						if (res && res.code === 200) {
							this.$message.success('新的员工信息已完成创建！')
							await this.fetchEmployeeList()
							this.resetForm()
							this.showAddDeptDialog = false
						} else {
							this.$message.error(res?.msg || '新增失败')
						}
					} else if (this.isEditMode) {
						const res = await updateStaff(formData)
						if (res && res.code === 200) {
							this.$message.success('更新成功')
							// 重新获取当前员工详情，更新数据
							await this.fetchEmployeeDetail(this.currentEmployee.id)
							// 重新获取员工列表
							await this.fetchEmployeeList()
							this.isEditMode = false
							this.isAddMode = false
							this.showAddDeptDialog = false
						} else {
							this.$message.error(res?.msg || '更新失败')
						}
					}
				} catch (error) {
					console.error('保存失败:', error)
					this.$message.error('保存失败')
				} finally {
					this.loading = false
				}
			},

			// 添加密码验证方法
			validateConfirmPassword(rule, value, callback) {
				if (value !== this.passwordForm.newPassword) {
					callback(new Error('两次输入密码不一致'))
				} else {
					callback()
				}
			},

			// 重置密码处理
			async handleResetPassword() {
			  this.$refs.passwordFormRef.validate(async (valid) => {
			    if (!valid) return
			    
			    this.resettingPassword = true
			    try {
			      const params = {
			        id: this.rightClickEmployee.id,
			        newPassword: this.passwordForm.newPassword
			      }
			      
			      const res = await editPwd(params)
			      if (res && res.code === 200) {
			        this.$message.success('本系统的登陆密码已重置，请重新登录')
			        
			        // 清空本地缓存
			        localStorage.clear()
			        sessionStorage.clear()
			        
			        // 跳转到登录页
			        this.$router.push('/login')
			      } else {
			        this.$message.error(res?.msg || '重置密码失败')
			      }
			    } catch (error) {
			      console.error('重置密码失败:', error)
			      this.$message.error('重置密码失败')
			    } finally {
			      this.resettingPassword = false
			      this.resetPasswordDialogVisible = false
			      this.resetPasswordForm()
			    }
			  })
			},
			
			// 重置密码表单
			resetPasswordForm() {
			  this.passwordForm = {
			    newPassword: '',
			    confirmPassword: ''
			  }
			  if (this.$refs.passwordFormRef) {
			    this.$refs.passwordFormRef.clearValidate()
			  }
			},
			
			validateForm() {
			  if (!this.form.fullName) {
			    this.$message.warning('请输入姓名')
			    return false
			  }
			
			  if (!this.form.idNumber) {
			    this.$message.warning('请输入身份证号码')
			    return false
			  }
			
			  if (!/^\d{17}[\dXx]$/.test(this.form.idNumber)) {
			    this.$message.warning('请输入正确的身份证号码')
			    return false
			  }
			
			  if (!this.form.contactNumber) {
			    this.$message.warning('请输入联系电话')
			    return false
			  }
			
			  if (!/^1[3-9]\d{9}$/.test(this.form.contactNumber)) {
			    this.$message.warning('请输入正确的手机号码')
			    return false
			  }
			
			  if (!this.form.professionalTitle) {
			    this.$message.warning('请选择专业职称')
			    return false
			  }
			
			  if (this.deptTableData.length === 0) {
			    this.$message.warning('请至少添加一个科室')
			    return false
			  }
			
			  return true
			},
			
			resetForm() {
			  this.form = {
			    anesthesiaAuthorization: 0,
			    antiTumorPermission: 0,
			    antibioticAuthorization: 0,
			    birthDate: '',
			    bloodType: '',
			    chPrescriptionRights: 0,
			    clinicId: this.userInfo.clinicId,
			    contactAddress: '',
			    contactNumber: '',
			    dateOfEmp: '',
			    deptList: [],
			    educationalBackground: '',
			    entryTime: '',
			    fullName: '',
			    idNumber: '',
			    idType: this.dictData.certypeEntities.find(item => item.id === 1 && item.name === '居民身份证')
			      ?.id || '',
			    lunarBirthDate: '',
			    major: '',
			    maritalStatus: '',
			    minZu: '',
			    nationality: this.dictData.country.find(item => item.id === 1 && item.name === '中国')?.id || '',
			    nativePlace: '',
			    pcElectronicImage: '',
			    poisonPermission: 0,
			    politicalStatus: '',
			    practiceDate: '',
			    practicingNumber: '',
			    prescriptionRights: 0,
			    professionalTitle: '',
			    qcElectronicImage: '',
			    qualificationNumber: '',
			    resignationTime: '',
			    roleId: [],
			    sex: '',
			    signatureImage: '',
			    spiritPermission: 0,
			    staffAtte: '',
			    standardCode: '',
			    standardName: '',
			    status: 1,
			    surgicalPermission: 0,
			    workIdPhoto: ''
			  }
			  this.deptTableData = []
			  this.isEditMode = false
			  this.isAddMode = false
			  this.showAddDeptDialog = false
			  this.nativePlaceCascader = []
			},
			
			handleDeptEdit(row) {
			  row.editing = true
			},
			
			// 修改保存科室编辑方法
			saveDeptEdit(row) {
			  if (!row.deptName) {
			    this.$message.warning('请选择科室名称')
			    return
			  }
			  
			  // 检查科室名称是否重复
			  if (!this.checkDuplicateDeptName(row.id)) {
			    return
			  }
			  
			  // 如果设为主要科室，检查是否已有主要科室
			  if (row.mainDep === 1) {
			    const existingMainDept = this.deptTableData.find(
			      dept => dept.id !== row.id && dept.mainDep === 1
			    )
			    if (existingMainDept) {
			      this.$message.warning('已存在主要科室: ' + existingMainDept.deptName)
			      row.mainDep = 0
			    }
			  }
			  
			  row.editing = false
			  this.$message.success('修改成功')
			  
			  // 自动验证主要科室设置
			  this.validateMainDept()
			},
			
			// 自动验证主要科室设置
			validateMainDept() {
			  const mainDepts = this.deptTableData.filter(dept => dept.mainDep === 1)
			  
			  // 如果没有主要科室，自动将第一个设为主要
			  if (mainDepts.length === 0 && this.deptTableData.length > 0) {
			    this.$nextTick(() => {
			      this.deptTableData[0].mainDep = 1
			      this.$message.info('已自动将第一个科室设为主要科室')
			    })
			  }
			  // 如果有多于一个主要科室，只保留第一个
			  else if (mainDepts.length > 1) {
			    this.$nextTick(() => {
			      // 找到所有主要科室
			      const mainDeptIndices = []
			      this.deptTableData.forEach((dept, index) => {
			        if (dept.mainDep === 1) {
			          mainDeptIndices.push(index)
			        }
			      })
			      
			      // 保留第一个，其他设为非主要
			      for (let i = 1; i < mainDeptIndices.length; i++) {
			        this.deptTableData[mainDeptIndices[i]].mainDep = 0
			      }
			      
			      this.$message.warning('只能有一个主要科室，已自动调整')
			    })
			  }
			},
			
			cancelDeptEdit(row) {
			  row.editing = false
			  this.fetchEmployeeDetail(this.currentEmployee.id)
			},
			
			// 修改移除科室方法
			handleDeptRemove(row) {
			  this.$confirm('确定要移除该科室吗？', '提示', {
			    confirmButtonText: '确定',
			    cancelButtonText: '取消',
			    type: 'warning'
			  }).then(() => {
			    const isMainDept = row.mainDep === 1
			    this.deptTableData = this.deptTableData.filter(item => item.id !== row.id)
			    this.$message.success('移除成功')
			    
			    // 如果移除的是主要科室，且还有其他科室，自动设置第一个科室为主要科室
			    if (isMainDept && this.deptTableData.length > 0) {
			      this.$nextTick(() => {
			        this.deptTableData[0].mainDep = 1
			        this.$message.info('已自动设置 ' + this.deptTableData[0].deptName + ' 为主要科室')
			      })
			    }
			  })
			},
			
			// 修改添加科室对话框显示逻辑
			cancelAddDept() {
			  this.showAddDeptDialog = false
			  this.newDept = {
			    deptName: '',
			    mainDep: 0
			  }
			},
			
			saveNewDept() {
			  if (!this.newDept.deptName) {
			    this.$message.warning('请选择科室名称')
			    return
			  }
			
			  const deptOption = this.deptOptions.find(dept => dept.deptName === this.newDept.deptName)
			  const newDept = {
			    id: Date.now(),
			    deptId: deptOption ? deptOption.id : 0,
			    deptName: this.newDept.deptName,
			    mainDep: this.newDept.mainDep,
			    editing: false
			  }
			
			  this.deptTableData.push(newDept)
			  this.showAddDeptDialog = false
			  this.newDept = {
			    deptName: '',
			    mainDep: 0
			  }
			  this.$message.success('科室添加成功')
			}
		},
		}
		</script>
		
		<style lang="less" scoped>
		.employee-container {
		  display: flex;
		  flex-direction: column;
		  height: calc(100vh - 100px);
		  overflow: hidden;
		  background-color: #f5f7fa;
		
		  .main-header {
		    background-color: #fff;
		    border-radius: 8px;
		    padding: 12px;
		    margin-bottom: 20px;
		    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		
		    .header-title {
		      display: flex;
		      align-items: center;
		      margin-bottom: 16px;
		
		      i {
		        font-size: 18px;
		        color: #409eff;
		        margin-right: 8px;
		      }
		
		      span {
		        font-size: 16px;
		        font-weight: 600;
		        color: #303133;
		        margin-right: 16px;
		      }
		
		      .add-btn {
		        padding: 6px 12px;
		      }
		    }
		  }
		  
		  .content-wrapper {
		    display: flex;
		    flex: 1;
		    overflow: hidden;
		    gap: 10px;
		    
		    .left-aside {
		      width: 12%;
		      display: flex;
		      flex-direction: column;
		      background-color: #fff;
		      border-radius: 8px;
		      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		      
		      .left-header {
		        padding: 12px;
		        border-bottom: 1px solid #ebeef5;
		        
		        .filter-container {
		          display: flex;
		          align-items: center;
		          gap: 8px;
		          
		          .el-dropdown-link {
		            cursor: pointer;
		            color: #409eff;
		            padding: 6px 12px;
		            border: 1px solid #dcdfe6;
		            border-radius: 4px;
		            display: inline-flex;
		            align-items: center;
		            font-size: 12px;
		            white-space: nowrap;
		            
		            &:hover {
		              border-color: #c6e2ff;
		              background-color: #ecf5ff;
		            }
		          }
		          
		          .search-input {
		            flex: 1;
		          }
		        }
		      }
		      
		      .employee-list {
		        flex: 1;
		        overflow-y: auto;
		        padding: 12px;
		        position: relative;
		        
		        .employee-item {
		          display: flex;
		          align-items: center;
		          padding: 8px;
		          margin-bottom: 6px;
		          border-radius: 6px;
		          cursor: pointer;
		          transition: all 0.3s ease;
		          border: 1px solid transparent;
		          position: relative;
		          
		          &:hover {
		            background-color: #f5f7fa;
		            border-color: #409eff;
		          }
		          
		          &.active {
		            background-color: #ecf5ff;
		            border-color: #409eff;
		            
		            .name {
		              color: #409eff;
		              font-weight: 600;
		            }
		          }
		          
		          .avatar-container {
		            margin-right: 8px;
		          }
		          
		          .info-container {
		            flex: 1;
		            min-width: 0;
		            position: relative;
		            
		            .name {
		              font-size: 12px;
		              color: #303133;
		              margin-bottom: 2px;
		              line-height: 1.4;
		              overflow: hidden;
		              text-overflow: ellipsis;
		              white-space: nowrap;
		              padding-right: 20px;
		            }
		            
		            .role {
		              font-size: 10px;
		              color: #909399;
		              line-height: 1.4;
		              overflow: hidden;
		              text-overflow: ellipsis;
		              white-space: nowrap;
		            }
		            
		            .badge-me {
		              position: absolute;
		              right: 0;
		              top: 0;
		              
		              /deep/ .el-badge__content {
		                background-color: #f56c6c;
		                border: none;
		                padding: 0 4px;
		                font-size: 10px;
		                height: 16px;
		                line-height: 16px;
		              }
		            }
		          }
		        }
		      }
		      
		      .pagination-container {
		        padding: 8px;
		        border-top: 1px solid #ebeef5;
		        display: flex;
		        justify-content: center;
		        background-color: #fff;
		      }
		    }
		    
		    .right-main {
		      flex: 1;
		      display: flex;
		      overflow: hidden;
		      
		      .content-left {
		        flex: 1;
		        display: flex;
		        flex-direction: column;
		        background-color: #fff;
		        border-radius: 8px;
		        overflow: hidden;
		        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		        
		        .skeleton-container {
		          padding: 20px;
		        }
		        
		        .employee-detail {
		          flex: 1;
		          display: flex;
		          flex-direction: column;
		          padding: 12px;
		          overflow: hidden;
		          
		          .detail-header {
		            display: flex;
		            align-items: center;
		            
		            .avatar-uploader {
		              /deep/ .el-upload {
		                border: 1px dashed #d9d9d9;
		                border-radius: 6px;
		                cursor: pointer;
		                position: relative;
		                overflow: hidden;
		                
		                &:hover {
		                  border-color: #409eff;
		                }
		                
		                .avatar-uploader-icon {
		                  font-size: 28px;
		                  color: #8c939d;
		                  width: 60px;
		                  height: 60px;
		                  line-height: 60px;
		                  text-align: center;
		                }
		                
		                .avatar {
		                  width: 60px;
		                  height: 60px;
		                  display: block;
		                }
		              }
		            }
		            
		            .detail-basic {
		              margin-left: 16px;
		              flex: 1;
		              
		              .detail-name {
		                margin: 0 0 4px 0;
		                font-size: 18px;
		                color: #303133;
		              }
		              
		              .detail-role {
		                font-size: 14px;
		                color: #606266;
		                
		                .badge {
		                  margin-left: 4px;
		                  vertical-align: top;
		                  
		                  /deep/ .el-badge__content {
		                    background-color: #909399;
		                    border: none;
		                    padding: 0 4px;
		                    font-size: 10px;
		                    height: 16px;
		                    line-height: 16px;
		                  }
		                }
		              }
		            }
		            
		            .edit-btn {
		              margin-left: auto;
		            }
		          }
		          
		          .el-divider {
		            margin: 8px 0;
		          }
		          
		          .detail-main-container {
		            display: flex;
		            flex: 1;
		            overflow: hidden;
		            flex-direction: column;
		            height: calc(100% - 120px);
		            
		            .scrollable-content-area {
		              flex: 1;
		              overflow-y: auto;
		              padding-right: 5px;
		              margin-bottom: 20px;
		              
		              .detail-module {
		                background: #fff;
		                border-radius: 8px;
		                padding: 20px;
		                box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		                border: 1px solid #ebeef5;
		                margin-bottom: 10px;
		                
		                .module-title {
		                  font-size: 16px;
		                  font-weight: 600;
		                  color: #303133;
		                  margin: 0 0 20px 0;
		                  padding-bottom: 10px;
		                  border-bottom: 2px solid #409eff;
		                  position: relative;
		                }
		                
		                .module-title:before {
		                  content: '';
		                  position: absolute;
		                  left: 0;
		                  bottom: -2px;
		                  width: 60px;
		                  height: 2px;
		                  background: #409eff;
		                }
		                
		                .module-content {
		                  margin-top: 10px;
		                }
		              }
		            }
		            
		            .fixed-bottom-area {
		              background: #fff;
		              border-radius: 8px;
		              box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
		              border: 1px solid #ebeef5;
		              padding: 20px;
		              margin-top: auto;
		              
		              .dept-module {
		                border: none;
		                box-shadow: none;
		                padding: 0;
		                
		                .dept-module-header {
		                  display: flex;
		                  align-items: center;
		                  justify-content: space-between;
		                  margin-bottom: 15px;
		                  
		                  .module-title {
		                    font-size: 16px;
		                    font-weight: 600;
		                    color: #303133;
		                    margin: 0;
		                    padding-bottom: 10px;
		                    border-bottom: 2px solid #409eff;
		                    position: relative;
		                    flex: 1;
		                  }
		                  
		                  .module-title:before {
		                    content: '';
		                    position: absolute;
		                    left: 0;
		                    bottom: -2px;
		                    width: 60px;
		                    height: 2px;
		                    background: #409eff;
		                  }
		                  
		                  .el-button {
		                    margin-left: 10px;
		                  }
		                }
		              }
		              
		              .action-buttons {
		                display: flex;
		                justify-content: flex-end;
		                gap: 12px;
		                margin-top: 20px;
		                padding-top: 20px;
		                border-top: 1px solid #ebeef5;
		                
		                /deep/ .el-button {
		                  padding: 6px 12px;
		                }
		              }
		            }
		          }
		          
		          .detail-info,
		          .certificate-info {
		            flex: 1;
		            overflow-y: auto;
		            
		            .info-row {
		              display: flex;
		              margin-bottom: 12px;
		              
		              .info-item {
		                flex: 1;
		                display: flex;
		                align-items: center;
		                margin-right: 16px;
		                
		                &:last-child {
		                  margin-right: 0;
		                }
		                
		                .label {
		                  min-width: 100px;
		                  font-size: 12px;
		                  color: #909399;
		                  margin-right: 12px;
		                  text-align: right;
		                  white-space: nowrap;
		                  
		                  .required {
		                    color: #f56c6c;
		                    margin-right: 2px;
		                  }
		                }
		                
		                .value {
		                  flex: 1;
		                  font-size: 12px;
		                  color: #303133;
		                  word-break: break-all;
		                }
		                
		                /deep/ .el-input,
		                /deep/ .el-select,
		                /deep/ .el-cascader {
		                  flex: 1;
		                }
		                
		                /deep/ .el-input__inner {
		                  font-size: 12px;
		                  height: 28px;
		                  line-height: 28px;
		                }
		              }
		            }
		          }
		
		          .certificate-images {
		            display: grid;
		            grid-template-columns: repeat(2, 1fr);
		            gap: 16px;
		            padding: 8px;
		            margin-top: 16px;
		
		            .image-item {
		              .image-title {
		                font-size: 12px;
		                color: #606266;
		                margin-bottom: 8px;
		                text-align: center;
		              }
		
		              .image-container {
		                height: 120px;
		                border: 1px solid #ebeef5;
		                border-radius: 4px;
		                display: flex;
		                align-items: center;
		                justify-content: center;
		                overflow: hidden;
		
		                .certificate-image {
		                  max-width: 100%;
		                  max-height: 100%;
		                  object-fit: contain;
		                }
		
		                .image-uploader {
		                  width: 100%;
		                  height: 100%;
		                  display: flex;
		                  align-items: center;
		                  justify-content: center;
		
		                  /deep/ .el-upload {
		                    width: 100%;
		                    height: 100%;
		                    display: flex;
		                    align-items: center;
		                    justify-content: center;
		                  }
		
		                  .image-empty {
		                    color: #c0c4cc;
		                    font-size: 12px;
		                    display: flex;
		                    flex-direction: column;
		                    align-items: center;
		                    justify-content: center;
		
		                    i {
		                      font-size: 20px;
		                      margin-bottom: 4px;
		                    }
		
		                    span {
		                      font-size: 10px;
		                    }
		                  }
		                }
		              }
		            }
		          }
		          
		          .right-card {
		            .card-header {
		              display: flex;
		              align-items: center;
		              justify-content: space-between;
		              margin-bottom: 12px;
		
		              .card-title {
		                margin: 0;
		                font-size: 16px;
		                color: #303133;
		                font-weight: 600;
		              }
		            }
		
		            .card-content {
		              /deep/ .el-table {
		                font-size: 12px;
		
		                th {
		                  padding: 8px 0;
		                }
		
		                td {
		                  padding: 8px 0;
		                }
		
		                .el-button--text {
		                  padding: 0;
		                  font-size: 12px;
		                }
		              }
		
		              .add-dept-dialog {
		                margin-top: 16px;
		                padding: 12px;
		                border: 1px dashed #dcdfe6;
		                border-radius: 4px;
						
						/* 添加主要科室提示样式 */
						.main-dept-tip {
						  margin: 8px 0;
						  padding: 8px;
						  background-color: #f0f9ff;
						  border-radius: 4px;
						  display: flex;
						  align-items: center;
						}
						
		                .dept-form {
		                  display: flex;
		                  gap: 8px;
		                  margin-bottom: 12px;
		                }
		
		                .dept-actions {
		                  display: flex;
		                  justify-content: flex-end;
		                  gap: 8px;
		                }
		              }
		            }
		          }
		
		          .action-buttons {
		            display: flex;
		            justify-content: flex-end;
		            gap: 12px;
		            margin-top: 16px;
		            padding-top: 12px;
		            border-top: 1px solid #ebeef5;
		
		            /deep/ .el-button {
		              padding: 6px 12px;
		            }
		          }
		        }
		
		        .empty-state {
		          display: flex;
		          flex-direction: column;
		          align-items: center;
		          justify-content: center;
		          height: 300px;
		
		          /deep/ .el-empty__description {
		            margin-top: 20px;
		          }
		        }
		      }
		    }
		  }
		  
		  /* 右键菜单样式 */
		  .right-context-menu {
		    animation: menuFadeIn 0.2s ease;
		    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
		    border-radius: 4px;
		    overflow: hidden;
		    /deep/ .el-card__body{
		      padding: 0;
		    }
		  }
		  
		  .right-menu-content {
		    display: flex;
		    flex-direction: column;
		    padding: 5px 0;
		  }
		  
		  .right-menu-content .el-button {
		    margin: 0;
		    border-radius: 0;
		    border: none;
		    color: #606266;
		    transition: all 0.2s;
		    display: flex;
		    align-items: center;
		    justify-content: flex-start;
		  }
		  
		  .right-menu-content .el-button:hover {
		    background-color: #f5f7fa;
		    color: #409eff;
		  }
		  
		  .right-menu-content .el-button i {
		    margin-right: 8px;
		    font-size: 14px;
		    width: 16px;
		    text-align: center;
		  }
		  
		  /* 确保菜单在最上层 */
		  .employee-container {
		    position: relative;
		    z-index: 1;
		  }
		  
		  .employee-list {
		    position: relative;
		    z-index: 1;
		  }
		  
		  /* 动画效果 */
		  @keyframes menuFadeIn {
		    from {
		      opacity: 0;
		      transform: scale(0.95);
		    }
		    to {
		      opacity: 1;
		      transform: scale(1);
		    }
		  }
		  
		  // 滚动条样式
		  ::-webkit-scrollbar {
		    width: 6px;
		    height: 6px;
		  }
		
		  ::-webkit-scrollbar-track {
		    background: #f1f1f1;
		    border-radius: 3px;
		  }
		
		  ::-webkit-scrollbar-thumb {
		    background: #c1c1c1;
		    border-radius: 3px;
		
		    &:hover {
		      background: #a8a8a8;
		    }
		  }
		  
		  /* 响应式布局 */
		  @media (max-width: 1200px) {
		    .detail-main-container {
		      flex-direction: column;
		    }
		    
		    .detail-right-panel {
		      max-width: 100%;
		    }
		  }
		}
		</style>